import { Component, HostListener, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { AsyncPipe } from '@angular/common'
import { ActivatedRoute } from '@angular/router'
import { TranslateDirective } from '@ngx-translate/core'
import { filter, map, switchMap } from 'rxjs'
import { CallDetailsBarComponent } from '../call-details-bar/call-details-bar.component'
import { CallSetupComponent } from '../call-setup/call-setup.component'
import { Call, CallsService } from '../calls.service'
import { TemplatesService } from '../templates.service'
import { UsersService } from '../users.service'
import { SpinnerComponent } from '../spinner/spinner.component'
import { ToolbarComponent } from '../toolbar/toolbar.component'
import { ComponentWithUnsavedChanges } from '../unsaved-changes.guard'

@Component({
  selector: 'app-call-setup-page',
  standalone: true,
  imports: [AsyncPipe, TranslateDirective, CallDetailsBarComponent, CallSetupComponent, SpinnerComponent, ToolbarComponent],
  templateUrl: './call-setup-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './call-setup-page.component.scss',
})
export class CallSetupPageComponent implements ComponentWithUnsavedChanges {
  @ViewChild(CallSetupComponent)
  callSetupComponent: CallSetupComponent | undefined = undefined

  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent) {
    if (this.hasUnsavedChanges) {
      event.preventDefault()
    }
  }

  private route = inject(ActivatedRoute)
  private callsService = inject(CallsService)
  private templatesService = inject(TemplatesService)
  private usersService = inject(UsersService)

  callId$ = this.route.params.pipe(
    map((params) => (typeof params['id'] === 'string' ? parseInt(params['id'], 10) : null)),
    filter((id) => id !== null)
  )
  call$ = this.callId$.pipe(switchMap((id) => this.callsService.getCall(id!)))
  templateItems$ = this.callId$.pipe(
    switchMap((callId) => this.templatesService.getTemplateItems(callId)),
    map((result) => (result.isSuccess ? result.data : []))
  )

  currentUser$ = this.usersService.currentUser$

  get hasUnsavedChanges(): boolean {
    return this.callSetupComponent?.hasUnsavedChanges ?? false
  }

  getDeactivateDialogData() {
    if (this.callSetupComponent?.hasUnconfirmedTemplate && !this.callSetupComponent?.templateEditor?.hasUnsavedChanges) {
      return {
        titleKey: 'MEETING_SETUP.UNCONFIRMED_DIALOG.TITLE',
        descriptionKey: 'MEETING_SETUP.UNCONFIRMED_DIALOG.DESCRIPTION',
        confirmLabelKey: 'MEETING_SETUP.UNCONFIRMED_DIALOG.LEAVE',
        cancelLabelKey: 'MEETING_SETUP.UNCONFIRMED_DIALOG.STAY',
      }
    }
    return null
  }

  getBackButtonLink(call: Call): string[] {
    if (call.isUpcoming || call.isOngoing) {
      return ['/app', 'meetings']
    }
    return ['/app', 'meetings', '' + call.id]
  }
}
